import { Injectable } from '@angular/core';
import { ApiResponse } from '@shared/models/base.model';
import { FreeTransactionParams } from '@shared/models/model';
import { filter, take } from 'rxjs';
import { ApiService } from './api.service';
import { LocalStorageService } from './local-storage.service';
import { Credetials } from './payment.model';

export const API_PAYMENT_ROUTES = {
  PAYMENT_MODES: '/secure/paymentmode/all',
  ACTIVE_NON_MEMBERS: '/secure/membership/nonmemberlist',
  ADD_UNCOMPLETED_PAID_TRANSACTION: '/secure/paypal/transaction',
  COMPLETE_PAID_TRANSACTION: '/secure/paypal/confirm/transaction',
  FREE_MEMBERSHIP_PURCHASE: '/secure/paypal/freemembership',
  CREDENTIALS: '/secure/site/credinfo',
  PAYPAL_CREDENTIALS: '/secure/site/credinfo?platform=paypal',
  FREE_TICKETS_PURCHASING: '/secure/event/freeeventbooking',
};

@Injectable({
  providedIn: 'root',
})
export class PaypalService {
  API_KEY?: string;

  private payment_modes: any[] = [];

  get paymentModes() {
    return this.payment_modes;
  }

  // Bug fix: PaypalService is a root singleton, instantiated the moment anything injects it -
  // AppComponent (mounted on every route, including /login) injects it for unrelated pending-
  // confirm recovery (see confirmTransaction usage there), which previously fired these two
  // /secure/** calls unconditionally at app bootstrap, before any login happened. Gated behind
  // LocalStorageService.session$ (emits the current jwt, or null when logged out) instead - fires
  // once a session actually exists, and again on every subsequent login (a fresh BehaviorSubject
  // emission), never while logged out.
  constructor(
    private _api: ApiService,
    private _storage: LocalStorageService,
  ) {
    this._storage.session$.pipe(filter((jwt) => !!jwt)).subscribe(() => {
      this.getPaymentModes()
        .pipe(take(1))
        .subscribe((res: any) => {
          this.payment_modes = res?.data;
        });

      this.getPaypalKeys()
        .pipe(take(1))
        .subscribe((res) => {
          if (res.success && res.data) {
            this.API_KEY = this.getApiKey(res.data);
          }
        });
    });
  }

  getPaymentModes() {
    return this._api.get<ApiResponse<any>>(`${API_PAYMENT_ROUTES.PAYMENT_MODES}`);
  }

  getPaypalModeId(): number | undefined {
    return this.payment_modes.find((i) => i.name === 'Paypal')?.id;
  }

  getFreeModeId(): number | undefined {
    return this.payment_modes.find((i) => i.name === 'Free')?.id;
  }

  // Not cached: this used to use getCachedResponse(), which caches the response in-memory
  // forever (no invalidation). Since this runs once per app load (see constructor below), a
  // browser tab open before an admin finished saving PayPal credentials would keep showing
  // "unable to accept payments" for its whole session even after the credentials were saved -
  // a plain fetch always reflects what's in the DB right now.
  getPaypalKeys() {
    return this._api.get<ApiResponse<Credetials>>(`${API_PAYMENT_ROUTES.PAYPAL_CREDENTIALS}`);
  }

  public addTransaction(data: any) {
    // no any in paypal service at all
    return this._api.post<any, ApiResponse<any>>(`${API_PAYMENT_ROUTES.ADD_UNCOMPLETED_PAID_TRANSACTION}`, data);
  }

  public freeTicketsTransaction(data: any) {
    // no any in paypal service at all
    return this._api.post<any, ApiResponse<any>>(`${API_PAYMENT_ROUTES.FREE_TICKETS_PURCHASING}`, data);
  }

  public confirmTransaction(data: any) {
    // no any in paypal service at all
    return this._api.post<any, ApiResponse<any>>(`${API_PAYMENT_ROUTES.COMPLETE_PAID_TRANSACTION}`, data);
  }

  // Bug F1 fix: backs the grace-period polling that replaces the old hard timer-expiry modal
  // close. Status-agnostic on the backend, so this reflects a completion from either the client
  // confirm call or the Paypal webhook.
  public getTransactionStatus(sqhubTransactionId: string) {
    return this._api.get<ApiResponse<{ sqhubTransactionId: string; transactionStatus: string }>>(
      `/secure/paypal/transaction/${sqhubTransactionId}/status`,
    );
  }

  public postFreeMembershipTransaction(data: FreeTransactionParams) {
    return this._api.post<FreeTransactionParams, ApiResponse<any>>(
      `${API_PAYMENT_ROUTES.FREE_MEMBERSHIP_PURCHASE}`,
      data,
    );
  }

  public getApiKey(credentials: Credetials): string | undefined {
    if (credentials && credentials.domainCredentialPropertiesList.length) {
      const key = credentials.isLive === 'Y' ? 'Production client id' : 'Sandbox client id';

      const index = credentials.domainCredentialPropertiesList.findIndex(
        (i) => i.credentialProperties.name === key && i.credentialProperties.type === 'Paypal',
      );

      if (index > -1) {
        return credentials.domainCredentialPropertiesList[index]?.value;
      }
    }

    return undefined;
  }
}
